import React from 'react';
import { useAppStateContext } from '../context/AppContext';
import { footerTranslations } from '../locales/footer';
import styles from './Footer.module.css';

// TypeScript interface for Footer props
interface FooterProps {
  copyrightText?: string;
  copyrightYear?: number;
  links?: Array<{
    text: string;
    href: string;
    external?: boolean;
    ariaLabel?: string;
  }>;
  language?: string;
  customTranslations?: any;
  className?: string;
  testId?: string;
}

/**
 * Footer Component
 *
 * A responsive and accessible footer component that provides:
 * - Copyright information with customizable text and year
 * - Navigation links with proper accessibility attributes
 * - Internationalization support via props and context
 * - High contrast and dark mode theme support
 * - Responsive layout for mobile and desktop
 *
 * @param {Object} props - Component props
 * @param {string} [props.copyrightText] - Custom copyright text (overrides default)
 * @param {number} [props.copyrightYear] - Custom copyright year (defaults to current year)
 * @param {Array} [props.links] - Array of link objects with { text, href, external, ariaLabel } properties
 * @param {string} [props.language] - Language override (defaults to context language)
 * @param {Object} [props.customTranslations] - Custom translations object
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.testId] - Test ID for testing purposes
 */
const Footer: React.FC<any> = ({
  copyrightText,
  copyrightYear,
  links,
  language,
  customTranslations,
  className = '',
  testId = 'footer',
}) => {
  // Get application state for theme and language
  const { highContrast, language: contextLanguage } = useAppStateContext();

  // Use provided language or fall back to context language or default
  const currentLanguage = language || contextLanguage || 'en';

  // Get translations, allowing custom translations to override defaults
  const baseTranslations =
    footerTranslations[currentLanguage as keyof typeof footerTranslations] || footerTranslations.en;
  const translations = {
    ...baseTranslations,
    ...customTranslations,
  };

  // Determine copyright text and year
  const year = copyrightYear || new Date().getFullYear();
  const copyright =
    copyrightText ||
    (translations.copyright
      ? translations.copyright.replace('{year}', year)
      : `© ${year} HelpGrandpaPhish`);

  // Default links if none provided
  const defaultLinks = [
    {
      text: translations.privacyPolicy,
      href: '/privacy',
      ariaLabel: translations.privacyPolicy,
},
    {
      text: translations.termsOfService,
      href: '/terms',
      ariaLabel: translations.termsOfService,
},
    {
      text: translations.aboutUs,
      href: '/about',
      ariaLabel: translations.aboutUs,
},
    {
      text: translations.help,
      href: '/help',
      ariaLabel: translations.help,
},
  ];

  // Use provided links, or default links if no links prop was provided
  // If empty array was explicitly provided, respect that choice
  const footerLinks = links !== undefined ? links : defaultLinks;

  // Only show navigation if there are links to display
  const showNavigation = footerLinks.length > 0;

  // Combine CSS classes based on theme
  const footerClasses = `
    ${(styles as any).footer}
    ${highContrast ? (styles as any).footerHighContrast : ''}
    ${className}
  `.trim();

  const linkClasses = (isExternal = false) =>
    `
    ${(styles as any).footerLink}
    ${highContrast ? (styles as any).footerLinkHighContrast : ''}
  `.trim();

  return (
    <footer
      className={footerClasses}
      role='contentinfo'
      aria-label={translations.footerNavigation || undefined}
      data-testid={testId}
    >
      <div className={(styles as any).footerContent}>
        <div className={(styles as any).footerSections}>
          {/* Copyright Section */}
          <div className={(styles as any).copyrightSection}>
            <p data-testid='footer-copyright'>{copyright}</p>
          </div>

          {/* Links Section */}
          {showNavigation && (
            <nav
              className={(styles as any).linksSection}
              aria-label={translations.footerLinks || undefined}
              data-testid='footer-navigation'
            >
              <div className={(styles as any).linkGroup}>
                {footerLinks.map((link: any, index: number) => {
                  const isExternal =
                    link.external || link.href.startsWith('http');

                  return (
                    <a
                      key={`footer-link-${index}`}
                      href={link.href}
                      className={linkClasses(isExternal)}
                      aria-label={link.ariaLabel || link.text || undefined}
                      data-testid={`footer-link-${index}`}
                      {...(isExternal && {
                        target: '_blank',
                        rel: 'noopener noreferrer',
                        'aria-describedby': `external-link-warning-${index}`,
                  })}
                    >
                      {link.text}
                      {/* Screen reader only text for external links */}
                      {isExternal && (
                        <span
                          id={`external-link-warning-${index}`}
                          className='sr-only'
                          aria-live='polite'
                        >
                          {` (${translations.externalLink})`}
                        </span>
                      )}
                    </a>
                  );
            })}
              </div>
            </nav>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
